import { Head, usePage } from '@inertiajs/react'
import { SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { TopBar } from './TopBar'
import { DashboardPageProps } from '@/types'

type BreadcrumbItem = {
  label: string
  href?: string
}

export type DashboardLayoutProps = {
  children: React.ReactNode
  title: string
  breadcrumbs?: BreadcrumbItem[]
  /** Primary page navigation shown as a left sidebar on desktop. */
  navigation?: React.ReactNode
  guide?: React.ReactNode
  mobileGuide?: React.ReactNode
  /**
   * Asides — `<Tip>` and friends. They belong in the secondary sidebar, never
   * inline: a drawn box in the middle of the content outweighs the content it
   * is commenting on, which is backwards for a note in the margin.
   */
  tips?: React.ReactNode
}

export function DashboardLayout({ children, title, breadcrumbs = [], navigation, guide, mobileGuide, tips }: DashboardLayoutProps) {
  const { flash, auth } = usePage<DashboardPageProps>().props
  const accountSlug = auth.account?.slug

  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: auth.account?.name || 'Your space', href: `/${accountSlug}/businesses` },
    { label: title },
  ]

  const finalBreadcrumbs = breadcrumbs.length > 0 ? breadcrumbs : defaultBreadcrumbs

  // Auto-prepend account base path to relative breadcrumb hrefs
  const resolvedBreadcrumbs = finalBreadcrumbs.map((item) => ({
    ...item,
    href: item.href && !item.href.startsWith('/') ? `/${accountSlug}/${item.href}` : item.href,
  }))

  return (
    <>
      <Head title={title} />
      <div className="font-display">
        <SidebarProvider
          className="flex-col"
          style={{ '--sidebar-width': '15rem', '--sidebar-width-mobile': '18rem' } as React.CSSProperties}
        >
          <TopBar />
          <div className="flex w-full flex-1">
            {navigation && (
              <aside
                data-dashboard-navigation
                className="hidden w-60 shrink-0 border-r border-border/40 px-4 py-10 lg:block"
              >
                <div className="sticky top-24">{navigation}</div>
              </aside>
            )}
            <main className="min-w-0 flex-1">
              {(flash.notice || flash.alert) && (
                <div className="border-b border-border/40 px-4 py-3">
                  {flash.notice && (
                    <div role="status" className="rounded-lg bg-blue-50 border border-blue-200/60 p-3 text-sm text-blue-700">
                      {flash.notice}
                    </div>
                  )}
                  {flash.alert && (
                    <div role="alert" className="rounded-lg bg-red-50 border border-red-200/60 p-3 text-sm text-red-700">
                      {flash.alert}
                    </div>
                  )}
                </div>
              )}

              <div className="flex-1 px-6 py-10 md:px-10 lg:px-14">
                <div className={cn('w-full', guide ? 'max-w-5xl' : 'max-w-[792px]')}>
                  {mobileGuide && <div className="mb-4 lg:hidden">{mobileGuide}</div>}
                  <Breadcrumb className="mb-4">
                    <BreadcrumbList>
                      {resolvedBreadcrumbs.map((item, index) => (
                        <span key={index} className="contents">
                          {index > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}
                          <BreadcrumbItem>
                            {item.href ? (
                              <BreadcrumbLink href={item.href}>{item.label}</BreadcrumbLink>
                            ) : (
                              <BreadcrumbPage>{item.label}</BreadcrumbPage>
                            )}
                          </BreadcrumbItem>
                        </span>
                      ))}
                    </BreadcrumbList>
                  </Breadcrumb>
                  {children}
                </div>
              </div>
            </main>
            {(guide || tips) && (
              // display:contents keeps this provider as a React context boundary
              // without adding a layout box. SidebarProvider renders a
              // min-h-svh w-full flex wrapper; nested inside the outer one it
              // becomes a flex sibling and claims row width the guide never uses,
              // squeezing the main column down to almost nothing.
              <SidebarProvider className="contents">
                <aside className="hidden w-64 shrink-0 px-6 py-10 lg:block">
                  {guide}
                  {/* Under whatever guidance the page already shows, so a wizard
                      keeps its step rail at the top where it is being used. */}
                  {tips && <div className={guide ? 'mt-8' : undefined}>{tips}</div>}
                </aside>
              </SidebarProvider>
            )}
          </div>
        </SidebarProvider>
      </div>
    </>
  )
}
